import {
	HttpErrorResponse,
	HttpEvent,
	HttpHandler,
	HttpInterceptor,
	HttpRequest,
	HttpStatusCode
} from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { AuthenticationModalComponent } from '@domain/authentication';
import { ModalController } from '@ionic/angular';
import { HttpService } from '../../http/http.service';
import { RefreshTokenRequest, RefreshTokenResponse } from '../dtos';
import { AppRoutingKeys, LoadingIndicatorState } from '@shared';
import { ConnectionProviderService } from '../services';
import { OverlayEventDetail } from '@ionic/core';
import { LoginResponse } from '../dtos'
import {
	BehaviorSubject,
	catchError,
	from,
	Observable,
	filter,
	switchMap,
	take,
	throwError,
	firstValueFrom,
	tap,
	of
} from 'rxjs';
import { LoadingIndicatorProps } from '../../../shared/loading-indicator/models/loading-indicator-props.model';
import { TokenProviderService } from '../services/token-provider.service';

@Injectable()
export class AuthInterceptorService implements HttpInterceptor {
    private isAuthInProgress = false;
    private authSubject: BehaviorSubject<string | null> = new BehaviorSubject<string | null>(null);

    public constructor(
        private tokenProvider: TokenProviderService,
        private modalController: ModalController,
        private loadingIndicatorState: LoadingIndicatorState,
		private httpService: HttpService,
		private connectionProvider: ConnectionProviderService,
        public router: Router,
    ) {}

    intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
        //console.log(`Intercepting request to ${request.url}`);
        if (request.url.includes('/refresh_token') || request.url.toLowerCase().includes('/authenticateuser')) {
            //console.log(`Bypassing interceptor for auth/refresh token request to ${request.url}`);
            return next.handle(request);
        }

        return this.tokenProvider.getAccessToken$().pipe(
            switchMap(token => {
                if (!token) {
                    //console.log(`No access token available, checking for JWT token for request to ${request.url}`);
                    return this.tokenProvider.get$().pipe(
                        switchMap(jwtToken => {
                            if (jwtToken) {
                                //console.log(`Using JWT token for request to ${request.url}`);
                                request = this.addTokenHeader(request, jwtToken);
                            } else {
                                console.log(`No JWT token found for request to ${request.url}`);
      							setTimeout(() => {
									this.loadingIndicatorState.setIsLoading(false);
								}, 1000);
                            }
                            return next.handle(request);
                        })
                    );
                } else {
                    //console.log(`Using access token for request to ${request.url}`);
                    request = this.addTokenHeader(request, token);
                    return next.handle(request);
                }
            }),
            catchError((error: HttpErrorResponse) => {
                if (error.status === HttpStatusCode.Unauthorized) {
                    //console.log(`Unauthorized request to ${request.url}, handling token refresh or re-authentication`);
                    return this.handleUnauthorizedError(request, next);
                } else {
                    //console.error(`Error for request to ${request.url}:`, error);
                    return throwError(() => error);
                }
            })
        );
    }

	private handleUnauthorizedError(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
		// Check if the refresh token exists
		return this.tokenProvider.getRefreshToken$().pipe(
			take(1),
			switchMap(refreshToken => {
				// If a refresh token exists, attempt to refresh the token
				if (refreshToken) {
					//console.log(`Refresh token found, attempting to refresh token for ${request.url}`);
					return this.handleRefreshTokenLogic(request, next);
				} else {
					// No refresh token available or dealing with old API version, prompt for re-authentication
					//console.log(`No refresh token found or dealing with old API version, prompting for re-authentication for ${request.url}`);
					return this.promptForReAuthentication(request, next);
				}
			}),
			catchError(error => {
				// Handle any errors encountered while checking for the refresh token or API version
				//console.error(`Error while handling unauthorized error for ${request.url}:`, error);
				return throwError(() => error);
			})
		);
	}

	private promptForReAuthentication(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
		if (this.isAuthInProgress) {
		  return this.waitForAuthToComplete(request, next);
		}
	
		this.isAuthInProgress = true;
		this.authSubject.next(null);
	
		return from(this.modalController.create({
		  component: AuthenticationModalComponent,
		  cssClass: 'auth-modal',
		  backdropDismiss: false
		})).pipe(
		  switchMap(modal => {
			modal.present();
			// Convert the promise to an observable and flatten it
			return from(modal.onDidDismiss()).pipe(
			  switchMap((overlayEventDetail) => {
				const newToken = overlayEventDetail.data; 
				if (newToken) {
				  this.tokenProvider.saveAccessToken(newToken);
				  this.isAuthInProgress = false;
				  this.authSubject.next(newToken);
				  // Directly return the Observable<HttpEvent<any>>
				  return next.handle(this.addTokenHeader(request, newToken));
				} else {
				  // Handle the case where re-authentication fails or is cancelled
				  this.isAuthInProgress = false;
				  return throwError(() => new Error('Failed to re-authenticate'));
				}
			  })
			);
		  }),
		  catchError(error => {
			this.isAuthInProgress = false;
			return throwError(() => error);
		  })
		);
	}	

	private waitForAuthToComplete(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
	return this.authSubject.pipe(
		filter(token => token !== null),
		take(1),
		switchMap(token => next.handle(this.addTokenHeader(request, token)))
	);
	}
	
	private handleRefreshTokenLogic(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
	
		if (this.isAuthInProgress) {
			//console.log('Authentication is already in progress');
			return this.authSubject.pipe(
				filter(token => token !== null),
				take(1),
				switchMap(token => {
					return next.handle(this.addTokenHeader(request, token));
				})
			);
		}
	
		this.isAuthInProgress = true;
		this.authSubject.next(null);
	
		return from(this.connectionProvider.get()).pipe(
			switchMap(connectionId => {
				return this.tokenProvider.getRefreshToken$().pipe(
					switchMap(refreshToken => {
						//console.log('Attempting to refresh token');
						return this.httpService.post<RefreshTokenRequest, RefreshTokenResponse>(
							connectionId,
							'refresh_token',
							new RefreshTokenRequest(refreshToken)
						).pipe(
							switchMap(response => {
								//console.log('Received new access token, saving and retrying request');
								this.tokenProvider.saveAccessToken(response.AccessToken);
								if (response.RefreshToken) {
									this.tokenProvider.saveRefreshToken(response.RefreshToken);
								}
								this.isAuthInProgress = false;
								this.authSubject.next(response.AccessToken);
								return next.handle(this.addTokenHeader(request, response.AccessToken));
							}),
							catchError(error => {
								console.error('Error during token refresh:', error);
								this.isAuthInProgress = false; // Reset the flag
							
								// Check if the error is a 401 using the actual structure of the error
								const isUnauthorized = error instanceof HttpErrorResponse && error.status === 401;
								const isCustomUnauthorized = error.httpStatusCode === 401; 
							
								if (isUnauthorized || isCustomUnauthorized) {
									//console.log('Refresh token expired, initiating user re-authentication');
									return this.handleRefreshTokenError(request, next);
								} else {
									return throwError(() => error);
								}
							})
						);
					}),
					catchError(error => {
						this.isAuthInProgress = false; // Ensure to reset the flag here as well
						console.error('Error fetching connectionId:', error);
						return throwError(() => new Error('Error fetching connectionId'));
					})
				);
			}),
		);
	}
	
	private handleRefreshTokenError(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
		if (this.isAuthInProgress) {
			console.log('Waiting for ongoing authentication process to complete');
			return this.authSubject.pipe(
				filter(token => token !== null),
				take(1),
				switchMap(token => next.handle(this.addTokenHeader(request, token)))
			);
		}

		this.isAuthInProgress = true;
		this.authSubject.next(null);

		return from(this.modalController.create({
			component: AuthenticationModalComponent,
			cssClass: 'auth-modal',
			backdropDismiss: false
		})).pipe(
			switchMap(modal => from(modal.present()).pipe(
				switchMap(() => modal.onDidDismiss<string>())
			)),
			switchMap((overlayEventDetail: OverlayEventDetail<string>) => {
			const accessToken = overlayEventDetail.data; 
			
				if (accessToken) {
					console.log('User re-authenticated');
					this.isAuthInProgress = false;
					this.authSubject.next(accessToken);
					return next.handle(this.addTokenHeader(request, accessToken));
				} else {
					console.log('No valid access token received');
					this.isAuthInProgress = false;
					return throwError(() => new Error('User did not re-authenticate'));
				}
			}),
			catchError(error => {
				console.error('Error during re-authentication:', error);
				this.isAuthInProgress = false;
				return throwError(() => error);
			})
		);
	}

    private addTokenHeader(request: HttpRequest<any>, token: string): HttpRequest<any> {
        return request.clone({
            setHeaders: { Authorization: `Bearer ${token}` }
        });
    }
}